import { payablePeriods } from "@chams-pay/application";
import type {
  AuthenticatedUserContract,
  LocalFinancialResultContract,
  Locale,
  OperationalPaymentContract,
  StudentLifecycleStatusContract,
  StudentProfileContract,
} from "@chams-pay/contracts";
import {
  normalizeSchoolLevelLabel,
  operationsMessages,
  paymentMessages,
  translate,
} from "@chams-pay/contracts";
import { Badge } from "@chams-pay/design-system/components/badge";
import { Button } from "@chams-pay/design-system/components/button";
import { Input } from "@chams-pay/design-system/components/input";
import { FinancialDocument } from "@chams-pay/document-ui";
import {
  ArrowLeft,
  ChevronDown,
  Funnel,
  Plus,
  RefreshCw,
  Search,
  UserPlus,
  UserRound,
  X,
} from "lucide-react";
import { A4DocumentPreview } from "../../ui/a4-document-preview.js";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { initialOf, periodLabel } from "../../core/format.js";
import {
  nextOpenScheduleYear,
  studentObligationYears,
} from "../../core/student-obligation-years.js";
import { useStudentServiceAmounts } from "../../core/use-operations.js";
import { usePayment } from "../../core/use-payment.js";
import { useStudentLifecycle } from "../../core/use-student-lifecycle.js";
import type { StudentSearchController } from "../../core/use-students.js";
import type { WorkspaceController } from "../../core/use-workspace.js";
import { deploymentLevelLabels } from "../../core/deployment.js";
import { FilterSelect } from "../../ui/filter-select.js";
import { Amount, Bidi, Count, DateValue, ServiceIcons } from "../../ui/figures.js";
import { StudentListSummary } from "../../ui/student-list-summary.js";
import { DesktopPaymentFlow } from "../payment.js";
import { OperationalPaymentSurface } from "../payment-detail.js";
import { DesktopStudentLifecycle } from "../student-lifecycle.js";
import {
  periodState,
  periodStateKey,
  StudentFormSurface,
  StudentsEmptyState,
  Surface,
  type PeriodState,
} from "../surfaces.js";

const stateDot: Record<PeriodState, string> = {
  paid: "bg-success",
  overdue: "bg-destructive",
  due: "bg-warning",
  upcoming: "bg-border",
};

const stateBadge: Record<PeriodState, "success" | "warning" | "destructive" | "neutral"> = {
  paid: "success",
  overdue: "destructive",
  due: "warning",
  upcoming: "neutral",
};

const serviceAmountFields = [
  { family: "registration", label: "sp.enrollmentFee" },
  { family: "tuition", label: "sp.tuition" },
  { family: "transport", label: "sp.transport" },
  { family: "accompaniment", label: "sp.accompaniment" },
] as const;

const lifecycleStatusKey: Record<
  StudentLifecycleStatusContract,
  "sp.statusActive" | "sp.statusWithdrawn" | "sp.statusGraduated"
> = {
  active: "sp.statusActive",
  withdrawn: "sp.statusWithdrawn",
  graduated: "sp.statusGraduated",
};

function classOptionsOf(_workspace: WorkspaceController): readonly string[] {
  return deploymentLevelLabels;
}

export function DesktopStudentsScreen({
  locale,
  online,
  csrf,
  user,
  students,
  workspace,
  onBack,
}: {
  readonly locale: Locale;
  readonly online: boolean;
  readonly csrf: string | null;
  readonly user: AuthenticatedUserContract | null;
  readonly students: StudentSearchController;
  readonly workspace: WorkspaceController;
  readonly onBack: () => void;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersRef = useRef<HTMLDivElement>(null);
  const canAdminister = Boolean(user) && online && Boolean(csrf);

  useEffect(() => {
    if (!filtersOpen) return;
    const dismiss = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (filtersRef.current?.contains(target)) return;
      if (target.closest('[data-slot="select-content"]')) return;
      setFiltersOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setFiltersOpen(false);
    };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", escape);
    };
  }, [filtersOpen]);

  if (students.selected)
    return (
      <DesktopStudentProfile
        key={students.selected.id}
        locale={locale}
        online={online}
        csrf={csrf}
        user={user}
        students={students}
        student={students.selected}
        workspace={workspace}
        onBack={onBack}
      />
    );

  const registerButton = canAdminister ? (
    <Button className="h-11" onClick={() => setRegisterOpen(true)}>
      <UserPlus />
      {t("sp.registerStudent")}
    </Button>
  ) : null;
  const emptyAction = students.filtersActive ? (
    <Button variant="outline" className="h-11" onClick={students.clearFilters}>
      <X />
      {t("sp.clearFilters")}
    </Button>
  ) : (
    registerButton
  );

  return (
    <section className="flex flex-col gap-5" data-testid="students-screen">
      <div className="overflow-visible rounded-lg border border-border bg-card shadow-e1">
        <div className="flex flex-wrap items-center gap-3.5 border-b border-border/70 px-5 py-4">
          <div className="flex flex-col gap-1">
            <span className="text-[11.5px] text-muted-foreground">
              <StudentListSummary locale={locale} students={students} />
            </span>
          </div>
          <div ref={filtersRef} className="relative ms-auto flex items-center gap-2">
            <form
              className="student-search chams-field-shell relative flex h-11 w-[330px] items-center gap-2.5 rounded-md border border-input bg-surface-2 px-3 transition-colors focus-within:border-primary focus-within:ring-3 focus-within:ring-primary/15"
              role="search"
              dir={locale === "ar" ? "rtl" : "ltr"}
              aria-busy={students.busy}
              onSubmit={(event) => {
                event.preventDefault();
                students.searchNow(students.query);
              }}
            >
              {students.busy ? (
                <span
                  aria-hidden="true"
                  className="size-3.5 shrink-0 animate-spin rounded-full border-2 border-teal-100 border-t-primary"
                />
              ) : (
                <Search aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
              )}
              <label className="flex-1">
                <span className="sr-only">{t("students.search")}</span>
                <input
                  type="search"
                  className="h-9 w-full min-w-0 bg-transparent text-start text-sm text-foreground outline-none placeholder:text-muted-2"
                  value={students.query}
                  placeholder={t("sp.searchHint")}
                  onChange={(event) => students.setQuery(event.target.value)}
                  aria-controls="student-results"
                />
              </label>
            </form>
            <Button
              type="button"
              size="icon"
              variant={students.filtersActive ? "default" : "outline"}
              className="size-11"
              aria-label={t("sp.filters")}
              aria-expanded={filtersOpen}
              onClick={() => setFiltersOpen((open) => !open)}
            >
              <Funnel />
            </Button>
            {filtersOpen && (
              <div className="absolute end-0 top-full z-40 mt-2 flex w-72 flex-col gap-3 rounded-lg border border-border bg-popover p-4 shadow-e3 animate-fade-up">
                <label className="flex flex-col gap-1.5 text-xs font-semibold text-muted-foreground">
                  {t("sp.class")}
                  <FilterSelect
                    value={students.filters.classLabel ?? "__all__"}
                    options={[
                      { value: "__all__", label: t("sp.filterAll") },
                      ...classOptionsOf(workspace).map((classLabel) => ({
                        value: classLabel,
                        label: classLabel,
                      })),
                    ]}
                    onChange={(value) =>
                      students.setFilters({
                        ...students.filters,
                        classLabel: value === "__all__" ? null : value,
                      })
                    }
                  />
                </label>
                <label className="flex flex-col gap-1.5 text-xs font-semibold text-muted-foreground">
                  {t("sp.lifecycleStatus")}
                  <FilterSelect
                    value={students.filters.lifecycleStatus}
                    options={[
                      { value: "all", label: t("sp.filterAll") },
                      { value: "active", label: t("sp.statusActive") },
                      { value: "withdrawn", label: t("sp.statusWithdrawn") },
                      { value: "graduated", label: t("sp.statusGraduated") },
                    ]}
                    onChange={(value) =>
                      students.setFilters({
                        ...students.filters,
                        lifecycleStatus: value as typeof students.filters.lifecycleStatus,
                      })
                    }
                  />
                </label>
                <label className="flex flex-col gap-1.5 text-xs font-semibold text-muted-foreground">
                  {t("sp.paymentStatus")}
                  <FilterSelect
                    value={students.filters.paymentStatus}
                    options={[
                      { value: "all", label: t("sp.filterAll") },
                      { value: "up-to-date", label: t("sp.upToDate") },
                      { value: "one-month-late", label: t("dash.oneMonth") },
                      { value: "two-months-late", label: t("dash.twoMonths") },
                      { value: "three-plus-months-late", label: t("dash.threePlus") },
                    ]}
                    onChange={(value) => {
                      const paymentStatus = value as typeof students.filters.paymentStatus;
                      students.setFilters({
                        ...students.filters,
                        paymentStatus,
                        arrears: null,
                        arrearsAsOf: null,
                        arrearsAcademicYear: null,
                      });
                    }}
                  />
                </label>
                {students.filters.arrears && (
                  <div className="flex items-center gap-2 rounded-md bg-warning-soft px-3 py-2 text-xs font-semibold text-warning">
                    <span className="min-w-0 flex-1 truncate">
                      {t(
                        students.filters.arrears === "one"
                          ? "dash.oneMonth"
                          : students.filters.arrears === "two"
                            ? "dash.twoMonths"
                            : students.filters.arrears === "three-plus"
                              ? "dash.threePlus"
                              : students.filters.arrears === "urgent"
                                ? "dash.urgentArrears"
                                : students.filters.arrears === "registration"
                                  ? "dash.registrationAttention"
                                  : "sp.filterArrears",
                      )}
                    </span>
                    <button
                      type="button"
                      className="grid size-7 place-items-center rounded-md hover:bg-warning/10"
                      aria-label={t("sp.clearFilters")}
                      onClick={() =>
                        students.setFilters({
                          ...students.filters,
                          arrears: null,
                          arrearsAsOf: null,
                          arrearsAcademicYear: null,
                        })
                      }
                    >
                      <X aria-hidden="true" className="size-3.5" />
                    </button>
                  </div>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={!students.filtersActive}
                  onClick={students.clearFilters}
                >
                  {t("sp.clearFilters")}
                </Button>
              </div>
            )}
          </div>
          {registerButton}
        </div>

        <div id="student-results" aria-live="polite">
          <div className="student-list__header grid grid-cols-[42px_minmax(180px,0.9fr)_repeat(3,minmax(110px,1fr))_112px] items-center gap-x-4 border-b border-border/70 bg-surface-2 px-5 py-2.5">
            <span aria-hidden="true" />
            <span className="chams-eyebrow">{t("sp.student")}</span>
            <span className="chams-eyebrow">{t("sp.class")}</span>
            <span className="chams-eyebrow">{t("students.massar")}</span>
            <span className="chams-eyebrow">{t("sp.reference")}</span>
            <span className="chams-eyebrow justify-self-start">{t("students.services")}</span>
          </div>
          {students.busy ? (
            <div className="flex flex-col">
              {[0, 1, 2].map((row) => (
                <div
                  key={row}
                  className="grid min-h-[68px] grid-cols-[42px_minmax(180px,0.9fr)_repeat(3,minmax(110px,1fr))_112px] items-center gap-x-4 border-b border-border/50 px-5 py-3"
                >
                  <span className="chams-skeleton size-[42px] shrink-0 rounded-lg" />
                  <span className="flex min-w-0 flex-col gap-2">
                    <span className="chams-skeleton block h-2.5 w-3/5" />
                    <span className="chams-skeleton block h-2 w-2/5" />
                  </span>
                  <span className="chams-skeleton block h-2.5 w-3/5" />
                  <span className="chams-skeleton block h-2.5 w-4/5" />
                  <span className="chams-skeleton block h-2.5 w-4/5" />
                  <span className="chams-skeleton block h-7 w-[60px]" />
                </div>
              ))}
            </div>
          ) : students.items.length ? (
            <div className="student-list flex flex-col">
              {students.items.map((student) => (
                <button
                  key={student.id}
                  type="button"
                  className="student-row grid min-h-[68px] w-full grid-cols-[42px_minmax(180px,0.9fr)_repeat(3,minmax(110px,1fr))_112px] items-center gap-x-4 border-b border-border/50 px-5 py-3 text-start transition-colors duration-fast last:border-b-0 hover:bg-sunken/70"
                  onClick={() => void students.openStudent(student.id)}
                >
                  <span className="student-row__avatar grid size-[42px] shrink-0 place-items-center rounded-lg bg-accent font-heading text-sm font-bold text-primary-strong">
                    {initialOf(locale === "ar" ? student.nameAr : student.nameFr)}
                  </span>
                  <span className="flex min-w-0 flex-col items-start gap-0.5">
                    <strong className="w-full truncate font-heading text-sm">
                      {locale === "ar" ? student.nameAr : student.nameFr}
                    </strong>
                    <small className="w-full truncate text-[11.5px] text-muted-foreground">
                      {locale === "ar" ? student.nameFr : student.nameAr}
                    </small>
                  </span>
                  <span className="flex min-w-0 flex-col items-start gap-1 text-start">
                    <Bidi className="student-row__class max-w-full truncate text-[12.5px] font-bold">
                      {normalizeSchoolLevelLabel(student.currentClass)}
                    </Bidi>
                    {student.lifecycleStatus !== "active" && (
                      <Badge
                        variant={student.lifecycleStatus === "graduated" ? "info" : "warning"}
                        className="px-1.5 py-0.5 text-[9px]"
                      >
                        {t(lifecycleStatusKey[student.lifecycleStatus])}
                      </Badge>
                    )}
                  </span>
                  <span className="min-w-0 text-start">
                    <Bidi className="student-row__massar max-w-full truncate text-[12px] font-semibold text-muted-foreground">
                      {student.massar || "—"}
                    </Bidi>
                  </span>
                  <span className="min-w-0 text-start">
                    <Bidi className="student-row__reference max-w-full truncate text-[12px] font-semibold text-muted-foreground">
                      {student.internalReference}
                    </Bidi>
                  </span>
                  <ServiceIcons
                    className="justify-self-start"
                    locale={locale}
                    summaryAr={student.serviceSummaryAr}
                    summaryFr={student.serviceSummaryFr}
                  />
                </button>
              ))}
            </div>
          ) : (
            <StudentsEmptyState
              locale={locale}
              filtered={students.filtersActive}
              action={emptyAction}
            />
          )}
        </div>
      </div>

      {canAdminister && csrf && (
        <StudentFormSurface
          locale={locale}
          csrf={csrf}
          open={registerOpen}
          classOptions={classOptionsOf(workspace)}
          onOpenChange={setRegisterOpen}
          onSaved={students.studentSaved}
          onOpenStudent={(id) => void students.openStudent(id)}
        />
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ */

function sumOf(values: readonly string[]): string {
  return values.reduce((total, value) => total + BigInt(value), 0n).toString();
}

function HistoryRow({
  locale,
  reference,
  method,
  date,
  amount,
  status,
  syncing = false,
  pending = false,
  informational = false,
  rejected = false,
  cancelled = false,
  onClick,
}: {
  readonly locale: Locale;
  readonly reference: string;
  readonly method: string;
  readonly date: string;
  readonly amount: string;
  readonly status: string;
  readonly syncing?: boolean;
  readonly pending?: boolean;
  readonly informational?: boolean;
  readonly rejected?: boolean;
  readonly cancelled?: boolean;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-cancelled={cancelled}
      className="grid min-h-14 w-full grid-cols-[minmax(0,1fr)_88px_minmax(112px,auto)] items-center gap-3 border-b border-border/50 px-5 py-3 text-start transition-colors duration-fast last:border-b-0 hover:bg-sunken/70 data-[cancelled=true]:bg-destructive-soft/40"
    >
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <strong
          data-cancelled={cancelled}
          className="truncate font-heading text-[12.5px] data-[cancelled=true]:text-muted-foreground data-[cancelled=true]:line-through"
        >
          <Bidi>{reference}</Bidi>
        </strong>
        <small className="text-[11px] text-muted-foreground">
          {method} · <DateValue value={date} />
        </small>
      </span>
      <strong
        data-cancelled={cancelled}
        className="text-end font-heading text-[13px] data-[cancelled=true]:text-muted-2 data-[cancelled=true]:line-through"
      >
        <Amount value={amount} locale={locale} />
      </strong>
      <Badge
        dot={!syncing}
        variant={
          cancelled || rejected
            ? "destructive"
            : syncing || informational
              ? "info"
              : pending
                ? "warning"
                : "success"
        }
        className="min-w-0 justify-self-end"
      >
        {syncing && (
          <span
            aria-hidden="true"
            className="size-2 animate-spin rounded-full border-[1.5px] border-current border-t-transparent"
          />
        )}
        {status}
      </Badge>
    </button>
  );
}

export function DesktopStudentProfile({
  locale,
  online,
  csrf,
  user,
  students,
  student,
  workspace,
  onBack,
}: {
  readonly locale: Locale;
  readonly online: boolean;
  readonly csrf: string | null;
  readonly user: AuthenticatedUserContract | null;
  readonly students: StudentSearchController;
  readonly student: StudentProfileContract;
  readonly workspace: WorkspaceController;
  readonly onBack: () => void;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const paymentText = paymentMessages(locale);
  const operationsText = operationsMessages(locale);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [advancePaymentOpen, setAdvancePaymentOpen] = useState(false);
  const [historicalPaymentOpen, setHistoricalPaymentOpen] = useState(false);
  const obligationYears = studentObligationYears(student);
  const historicalYears = obligationYears.historicalYears;
  const [historicalCalendarId, setHistoricalCalendarId] = useState(
    historicalYears[0]?.calendarSnapshotId ?? "__no-historical-year__",
  );
  const [openScheduleYear, setOpenScheduleYear] = useState<string | null>(
    obligationYears.showCurrentYear
      ? student.calendar.id
      : (historicalYears[0]?.calendarSnapshotId ?? null),
  );
  const [editOpen, setEditOpen] = useState(false);
  const [amountsOpen, setAmountsOpen] = useState(false);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [historyDocument, setHistoryDocument] = useState<LocalFinancialResultContract | null>(null);
  const [operationalPayment, setOperationalPayment] = useState<OperationalPaymentContract | null>(
    null,
  );
  const payment = usePayment({
    student,
    actorUserId: students.paymentActorId,
    trustedObservedAt: workspace.replica.synchronizedAt,
    captureReadiness: workspace.readiness,
    finalizedPayments: students.unsynchronizedHistory,
    calendarSnapshotId: student.calendar.id,
    onQueueChanged: () => void workspace.refreshLocalStatus(),
    onFinalized: () => void students.reloadLocalHistory(),
  });
  const advancePayment = usePayment({
    student,
    actorUserId: students.paymentActorId,
    trustedObservedAt: workspace.replica.synchronizedAt,
    captureReadiness: workspace.readiness,
    finalizedPayments: students.unsynchronizedHistory,
    calendarSnapshotId: student.advanceYear?.calendarSnapshotId ?? "__no-advance-year__",
    onQueueChanged: () => void workspace.refreshLocalStatus(),
    onFinalized: () => void students.reloadLocalHistory(),
  });
  const historicalPayment = usePayment({
    student,
    actorUserId: students.paymentActorId,
    trustedObservedAt: workspace.replica.synchronizedAt,
    captureReadiness: workspace.readiness,
    finalizedPayments: students.unsynchronizedHistory,
    calendarSnapshotId: historicalCalendarId,
    onQueueChanged: () => void workspace.refreshLocalStatus(),
    onFinalized: () => void students.reloadLocalHistory(),
  });
  const canAdminister = Boolean(user) && online && Boolean(csrf);
  const canOperate = online && Boolean(csrf) && Boolean(user);
  const serviceAmounts = useStudentServiceAmounts({
    student,
    csrf: csrf ?? "",
    onSaved: (result) => {
      students.applyServiceAmounts(result);
      setAmountsOpen(false);
    },
  });
  const lifecycle = useStudentLifecycle({
    student,
    csrf: csrf ?? "",
    onSaved: students.studentSaved,
  });

  const periods = payablePeriods(payment.obligations);
  const advancePeriods = payablePeriods(advancePayment.obligations);
  const historicalYear = historicalYears.find(
    (year) => year.calendarSnapshotId === historicalCalendarId,
  );
  const unpaid = periods.filter((entry) => entry.remainingMad !== "0");
  const overdue = unpaid.filter((entry) => entry.overdue);
  const monthly = periods.find((entry) => /^\d{4}-\d{2}$/.test(entry.period));
  const settled = sumOf(payment.obligations.map((obligation) => obligation.settledMad));
  const advanceOutstanding = sumOf(
    advancePayment.obligations.map((obligation) => obligation.remainingMad),
  );
  const localByPaymentId = new Map(
    students.localHistory.map((entry) => [entry.payment.id, entry] as const),
  );

  const toggleGroup = (key: string) =>
    setExpanded((current) => (current.has(key) ? new Set() : new Set([key])));
  const toggleScheduleYear = (calendarSnapshotId: string) => {
    setOpenScheduleYear((current) => nextOpenScheduleYear(current, calendarSnapshotId));
    setExpanded(new Set());
  };
  const scheduleRows = (
    entries: typeof periods,
    remainingMad: string,
    paymentAction?: ReactNode,
  ) => (
    <>
      {entries.map((entry) => {
        const state = periodState(entry);
        const open = expanded.has(entry.period);
        const fullAmount = sumOf(entry.obligations.map((obligation) => obligation.netMad));
        return (
          <div
            key={entry.period}
            data-state={state}
            className="flex flex-col border-b border-border/50 last:border-b-0 data-[state=overdue]:bg-[#fdf9f9] data-[state=upcoming]:opacity-75"
          >
            <button
              type="button"
              aria-expanded={open}
              onClick={() => toggleGroup(entry.period)}
              className="grid min-h-14 w-full grid-cols-[8px_minmax(116px,1fr)_minmax(132px,auto)_minmax(108px,auto)_24px] items-center gap-3.5 px-5 py-3 text-start transition-colors hover:bg-sunken/50"
            >
              <span
                aria-hidden="true"
                className={`size-2 shrink-0 rounded-full ${stateDot[state]}`}
              />
              <span className="min-w-0 truncate font-heading text-[12.5px] font-bold">
                {periodLabel(entry.period, locale)}
              </span>
              <span className="flex flex-col items-end gap-0.5 text-end">
                <strong className="font-heading text-[12.5px]">
                  <Amount value={entry.remainingMad} locale={locale} />
                </strong>
                <small className="text-[9.5px] text-muted-foreground">
                  {t("sp.settled")} <Amount value={entry.settledMad} locale={locale} /> ·{" "}
                  {t("sp.total")} <Amount value={fullAmount} locale={locale} />
                </small>
              </span>
              <Badge dot variant={stateBadge[state]} className="justify-self-start">
                {t(periodStateKey[state])}
              </Badge>
              <ChevronDown
                aria-hidden="true"
                className={`size-4 justify-self-end text-muted-2 transition-transform ${
                  open ? "rotate-180" : ""
                }`}
              />
            </button>
            {open && (
              <div className="flex animate-fade-up flex-col border-t border-border/30 bg-surface-2">
                {entry.obligations.map((obligation) => (
                  <div
                    key={obligation.id}
                    className="grid min-h-11 grid-cols-[8px_minmax(116px,1fr)_minmax(132px,auto)_minmax(108px,auto)_24px] items-center gap-3.5 border-b border-border/30 px-5 py-2 last:border-b-0"
                  >
                    <span />
                    <span className="min-w-0 truncate text-xs text-muted-foreground">
                      {locale === "ar" ? obligation.labelAr : obligation.labelFr}
                    </span>
                    <span className="flex flex-col items-end gap-0.5 text-end">
                      <strong className="font-heading text-xs">
                        <Amount value={obligation.remainingMad} locale={locale} />
                      </strong>
                      <small className="text-[9.5px] text-muted-foreground">
                        {t("sp.settled")} <Amount value={obligation.settledMad} locale={locale} /> ·{" "}
                        {t("sp.total")} <Amount value={obligation.netMad} locale={locale} />
                      </small>
                    </span>
                    <span />
                    <span />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
      <div className="flex min-h-12 flex-wrap items-center gap-3 bg-surface-2 px-5 py-3">
        <span className="chams-eyebrow">{t("sp.remainingYear")}</span>
        <strong className="ms-auto font-heading text-[15px]">
          <Amount value={remainingMad} locale={locale} />
        </strong>
        {paymentAction}
      </div>
    </>
  );

  return (
    <section className="student-profile flex flex-col gap-4" data-testid="student-profile">
      <button
        type="button"
        className="back-button inline-flex h-11 items-center gap-1.5 self-start rounded-md px-2 text-sm font-bold text-primary-strong transition-colors hover:bg-accent"
        onClick={() => {
          setPaymentOpen(false);
          setAdvancePaymentOpen(false);
          setHistoricalPaymentOpen(false);
          onBack();
        }}
      >
        <ArrowLeft aria-hidden="true" className="size-4 rtl:-scale-x-100" />
        {t("students.back")}
      </button>

      <header className="student-identity-header flex flex-wrap items-center gap-5 rounded-lg border border-border bg-card px-6 py-5 shadow-e1">
        <span className="profile-avatar grid size-[62px] shrink-0 place-items-center rounded-2xl bg-primary-soft font-heading text-[22px] font-bold text-primary-strong">
          {initialOf(locale === "ar" ? student.nameAr : student.nameFr)}
        </span>
        <div className="student-identity-header__names flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex min-w-0 flex-col items-start gap-0.5">
            <h2 className="w-full truncate font-heading text-[23px] font-bold">
              {locale === "ar" ? student.nameAr : student.nameFr}
            </h2>
            <span className="w-full truncate text-sm text-muted-foreground">
              {locale === "ar" ? student.nameFr : student.nameAr}
            </span>
          </div>
          <div className="student-identity-header__badges flex flex-wrap items-center gap-2.5 text-[11.5px] font-bold text-muted-foreground">
            <span className="rounded-sm bg-surface-2 px-2 py-1">
              <Bidi>{normalizeSchoolLevelLabel(student.enrollment.classLabel)}</Bidi>
            </span>
            {student.massar && (
              <span className="rounded-sm bg-surface-2 px-2 py-1">
                <Bidi>MASSAR {student.massar}</Bidi>
              </span>
            )}
            <span className="student-identity-header__reference rounded-sm bg-surface-2 px-2 py-1">
              <Bidi>{student.internalReference}</Bidi>
            </span>
            <Badge
              variant={
                student.lifecycleStatus === "active"
                  ? "success"
                  : student.lifecycleStatus === "graduated"
                    ? "info"
                    : "warning"
              }
            >
              {t(lifecycleStatusKey[student.lifecycleStatus])}
            </Badge>
            <span className="rounded-sm bg-teal-50 px-2 py-1 text-primary-strong">
              {locale === "ar"
                ? student.serviceSummaryAr.replaceAll("المواكبة", "المرافقة")
                : student.serviceSummaryFr}
            </span>
          </div>
        </div>
        <div className="student-identity-header__actions flex shrink-0 flex-wrap items-center gap-2.5">
          {canAdminister && (
            <Button variant="outline" onClick={() => setEditOpen(true)}>
              <UserRound />
              {t("sp.editStudent")}
            </Button>
          )}
          {students.paymentActorId && (
            <Button
              className="student-payment-action h-[46px] px-5 text-[13.5px]"
              disabled={payment.blocked}
              onClick={() => setPaymentOpen(true)}
            >
              <Plus />
              {t("sp.takePayment")}
            </Button>
          )}
        </div>
      </header>

      {payment.blocked && (
        <div className="flex items-start gap-3 rounded-lg border border-destructive-border bg-destructive-soft px-5 py-4">
          <span
            aria-hidden="true"
            className="grid size-5 shrink-0 place-items-center rounded-full bg-destructive font-heading text-xs font-extrabold text-white"
          >
            !
          </span>
          <div className="flex flex-col items-start gap-1.5">
            <strong className="font-heading text-[12.5px] text-destructive">
              {t(
                workspace.captureBlockReason === "storage"
                  ? "sp.storageBlockedTitle"
                  : workspace.captureBlockReason === "clock"
                    ? "sp.clockBlockedTitle"
                    : "sp.blockedTitle",
              )}
            </strong>
            <p className="text-[11.5px] leading-relaxed text-destructive">
              {t(
                workspace.captureBlockReason === "storage"
                  ? "sp.storageBlockedBody"
                  : workspace.captureBlockReason === "clock"
                    ? "sp.clockBlockedBody"
                    : "sp.blockedBody",
              )}
            </p>
            <Button
              variant="destructive-solid"
              size="sm"
              className="mt-1.5 min-h-11"
              disabled={!workspace.canSync || workspace.syncing}
              onClick={() => void workspace.sync()}
            >
              <RefreshCw className={workspace.syncing ? "animate-spin" : ""} />
              {t("sp.syncNow")}
            </Button>
          </div>
        </div>
      )}

      <section className="student-financial-summary grid gap-3.5 sm:grid-cols-3">
        <div className="flex flex-col gap-2 rounded-lg border border-border border-t-[3px] border-t-destructive bg-card px-5 py-4 shadow-e1">
          <span className="chams-eyebrow">{t("sp.outstanding")}</span>
          <strong className="font-heading text-[30px] leading-none">
            <Amount value={student.obligationSummary.outstandingMad} locale={locale} />
          </strong>
          <small className="text-[11px] text-muted-foreground">
            {unpaid
              .slice(0, 3)
              .map((entry) => periodLabel(entry.period, locale))
              .join(" · ")}
          </small>
        </div>
        <div className="flex flex-col gap-2 rounded-lg border border-border bg-card px-5 py-4 shadow-e1">
          <span className="chams-eyebrow">{t("sp.overdueCount")}</span>
          <strong className="font-heading text-[30px] leading-none text-warning">
            <Count value={student.obligationSummary.overdueCount} />
          </strong>
          <small className="text-[11px] text-muted-foreground">
            {overdue.map((entry) => periodLabel(entry.period, locale)).join(" · ")}
          </small>
        </div>
        <div className="flex flex-col gap-2 rounded-lg border border-border bg-card px-5 py-4 shadow-e1">
          <span className="chams-eyebrow">{t("sp.yearTotal")}</span>
          <strong className="font-heading text-[30px] leading-none">
            <Amount value={student.obligationSummary.currentYearTotalMad} locale={locale} />
          </strong>
          <small className="text-[11px] text-muted-foreground">
            {t("sp.paidThisYear")} <Amount value={settled} locale={locale} />
          </small>
        </div>
      </section>

      <div className="student-workspace-grid grid items-start gap-4 xl:grid-cols-[1.25fr_1fr]">
        <section className="student-section overflow-hidden rounded-lg border border-border bg-card shadow-e1">
          <div className="student-section__heading flex flex-wrap items-center gap-2.5 border-b border-border/70 px-5 py-4">
            <div className="flex items-baseline gap-2.5">
              <h3 className="font-heading text-[15px] font-bold">{t("sp.feeSchedule")}</h3>
              {monthly && (
                <span className="text-[11px] text-muted-foreground">
                  {t("sp.perMonth")}{" "}
                  <Amount
                    value={sumOf(monthly.obligations.map((item) => item.netMad))}
                    locale={locale}
                  />
                </span>
              )}
            </div>
            {canOperate && csrf && student.lifecycleStatus !== "graduated" && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="ms-auto"
                aria-expanded={amountsOpen}
                onClick={() => setAmountsOpen((open) => !open)}
              >
                {t("sp.manageServiceAmounts")}
              </Button>
            )}
          </div>
          <div className="compact-obligation-list flex flex-col">
            {historicalYears.map((year) => {
              const yearObligations =
                year.calendarSnapshotId === historicalCalendarId
                  ? historicalPayment.obligations
                  : student.obligations.filter(
                      (obligation) => obligation.calendarSnapshotId === year.calendarSnapshotId,
                    );
              const yearPeriods = payablePeriods(yearObligations);
              const yearOutstanding = sumOf(
                yearObligations.map((obligation) => obligation.remainingMad),
              );
              return (
                <div key={year.calendarSnapshotId}>
                  <button
                    type="button"
                    aria-expanded={openScheduleYear === year.calendarSnapshotId}
                    className="flex min-h-14 w-full items-center gap-3 border-b border-border bg-card px-5 text-start"
                    onClick={() => {
                      setHistoricalCalendarId(year.calendarSnapshotId);
                      toggleScheduleYear(year.calendarSnapshotId);
                    }}
                  >
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <strong className="font-heading text-sm">
                        {t("sp.historicalYearGroup")}
                      </strong>
                      <Bidi className="text-[11px] text-muted-foreground">{year.academicYear}</Bidi>
                    </span>
                    <ChevronDown
                      className={`size-4 text-muted-2 transition-transform ${
                        openScheduleYear === year.calendarSnapshotId ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {openScheduleYear === year.calendarSnapshotId &&
                    scheduleRows(
                      yearPeriods,
                      yearOutstanding,
                      students.paymentActorId ? (
                        <Button
                          type="button"
                          size="sm"
                          disabled={historicalPayment.blocked}
                          onClick={() => setHistoricalPaymentOpen(true)}
                        >
                          {t("sp.takeHistoricalYearPayment")}
                        </Button>
                      ) : undefined,
                    )}
                </div>
              );
            })}
            {obligationYears.showCurrentYear && (
              <>
                <button
                  type="button"
                  aria-expanded={openScheduleYear === student.calendar.id}
                  className="flex min-h-14 items-center gap-3 border-b border-border bg-card px-5 text-start"
                  onClick={() => toggleScheduleYear(student.calendar.id)}
                >
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <strong className="font-heading text-sm">{t("sp.currentYearGroup")}</strong>
                    <Bidi className="text-[11px] text-muted-foreground">
                      {student.calendar.academicYear}
                    </Bidi>
                  </span>
                  <ChevronDown
                    className={`size-4 text-muted-2 transition-transform ${
                      openScheduleYear === student.calendar.id ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openScheduleYear === student.calendar.id &&
                  scheduleRows(periods, student.obligationSummary.outstandingMad)}
              </>
            )}
            {obligationYears.showAdvanceYear &&
              student.advanceYear &&
              advancePeriods.length > 0 && (
                <>
                  <button
                    type="button"
                    aria-expanded={openScheduleYear === student.advanceYear.calendarSnapshotId}
                    className="flex min-h-14 items-center gap-3 border-y border-border bg-card px-5 text-start"
                    onClick={() => toggleScheduleYear(student.advanceYear!.calendarSnapshotId)}
                  >
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <strong className="font-heading text-sm">{t("sp.nextYearGroup")}</strong>
                      <Bidi className="text-[11px] text-muted-foreground">
                        {student.advanceYear.academicYear}
                      </Bidi>
                    </span>
                    <ChevronDown
                      className={`size-4 text-muted-2 transition-transform ${
                        openScheduleYear === student.advanceYear!.calendarSnapshotId
                          ? "rotate-180"
                          : ""
                      }`}
                    />
                  </button>
                  {openScheduleYear === student.advanceYear.calendarSnapshotId &&
                    scheduleRows(
                      advancePeriods,
                      advanceOutstanding,
                      students.paymentActorId ? (
                        <Button
                          type="button"
                          size="sm"
                          disabled={advancePayment.blocked}
                          onClick={() => setAdvancePaymentOpen(true)}
                        >
                          {t("sp.takeNextYearPayment")}
                        </Button>
                      ) : undefined,
                    )}
                </>
              )}
            {obligationYears.allPaidAfterGraduation && (
              <p className="px-5 py-8 text-center text-sm text-muted-foreground">
                {t("sp.allObligationsPaid")}
              </p>
            )}
          </div>
        </section>

        <div className="flex flex-col gap-4">
          <DesktopStudentLifecycle
            locale={locale}
            student={student}
            controller={lifecycle}
            editable={canAdminister}
          />
          <section className="student-section overflow-hidden rounded-lg border border-border bg-card shadow-e1">
            <div className="student-section__heading border-b border-border/70 px-5 py-4 font-heading text-[15px] font-bold">
              {t("sp.paymentHistory")}
            </div>
            {online &&
            user &&
            (students.serverHistory.length > 0 || students.unsynchronizedHistory.length > 0) ? (
              <div className="compact-payment-history flex flex-col">
                {students.serverHistory
                  .slice(0, Math.max(0, 5 - students.unsynchronizedHistory.length))
                  .map((entry) => {
                    const local = localByPaymentId.get(entry.payment.id);
                    const pending =
                      local?.queueState !== undefined && local.queueState !== "synced";
                    return (
                      <HistoryRow
                        key={entry.payment.id}
                        locale={locale}
                        reference={entry.payment.reference}
                        method={paymentText[entry.payment.method.method]}
                        date={entry.payment.receivedDate}
                        amount={entry.payment.amountMad}
                        status={
                          pending && local
                            ? paymentText[local.queueState]
                            : operationsText[entry.lifecycle]
                        }
                        syncing={pending}
                        pending={entry.lifecycle === "pending"}
                        rejected={entry.lifecycle === "rejected"}
                        cancelled={entry.lifecycle === "cancelled"}
                        onClick={() => setOperationalPayment(entry)}
                      />
                    );
                  })}
                {students.unsynchronizedHistory.slice(0, 5).map((entry) => (
                  <HistoryRow
                    key={entry.payment.id}
                    locale={locale}
                    reference={entry.payment.reference}
                    method={paymentText[entry.payment.method.method]}
                    date={entry.payment.receivedDate}
                    amount={entry.payment.amountMad}
                    status={paymentText[entry.queueState]}
                    syncing={entry.queueState === "syncing"}
                    informational={entry.queueState !== "synced"}
                    pending={entry.payment.settlementStatus === "pending"}
                    onClick={() => setHistoryDocument(entry)}
                  />
                ))}
              </div>
            ) : !online && students.localHistory.length ? (
              <div className="compact-payment-history flex flex-col">
                {students.localHistory.slice(0, 5).map((entry) => (
                  <HistoryRow
                    key={entry.payment.id}
                    locale={locale}
                    reference={entry.payment.reference}
                    method={paymentText[entry.payment.method.method]}
                    date={entry.payment.receivedDate}
                    amount={entry.payment.amountMad}
                    status={paymentText[entry.queueState]}
                    syncing={entry.queueState === "syncing"}
                    informational={entry.queueState !== "synced"}
                    pending={entry.payment.settlementStatus === "pending"}
                    onClick={() => setHistoryDocument(entry)}
                  />
                ))}
              </div>
            ) : (
              <div className="student-section__empty px-5 py-10 text-center text-sm text-muted-foreground">
                {t("students.noPayments")}
              </div>
            )}
          </section>

          <section className="student-section student-details flex flex-col gap-3 rounded-lg border border-border bg-card px-5 py-4 shadow-e1">
            <h3 className="font-heading text-[15px] font-bold">{t("sp.details")}</h3>
            <dl className="flex flex-col gap-2.5 text-xs text-muted-foreground">
              <div className="flex items-center justify-between gap-3">
                <dt>{t("sp.guardian")}</dt>
                <dd className="font-heading font-bold text-foreground">
                  {student.primaryGuardian.name}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt>{t("sp.birthDate")}</dt>
                <dd className="font-bold text-foreground">
                  <DateValue value={student.birthDate} />
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt>{t("sp.enrolledOn")}</dt>
                <dd className="font-bold text-foreground">
                  <DateValue value={student.enrollment.enrolledAt.slice(0, 10)} />
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt>{t("students.enrollment")}</dt>
                <dd className="font-bold text-foreground">
                  <Bidi>{student.enrollment.academicYear}</Bidi>
                </dd>
              </div>
            </dl>
          </section>
        </div>
      </div>

      <Surface
        open={amountsOpen && canOperate && Boolean(csrf)}
        onOpenChange={setAmountsOpen}
        title={t("sp.manageServiceAmounts")}
        description={t("sp.manageServiceAmountsHelp")}
        closeLabel={t("common.close")}
        testId="student-service-amounts-surface"
        footer={
          <div className="flex flex-1 items-center gap-2">
            <Button type="button" variant="outline" onClick={() => setAmountsOpen(false)}>
              {t("ops.cancel")}
            </Button>
            <Button
              form="student-service-amounts-form"
              type="submit"
              className="ms-auto"
              disabled={serviceAmounts.submitDisabled}
            >
              {t("sp.saveServiceAmounts")}
            </Button>
          </div>
        }
      >
        <form
          id="student-service-amounts-form"
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            void serviceAmounts.submit();
          }}
        >
          {serviceAmounts.academicYears.length > 1 && (
            <label className="flex flex-col gap-1.5 text-[11.5px] font-bold">
              {t("students.academicYear")}
              <select
                className="h-11 rounded-md border border-input bg-card px-3 font-heading text-sm"
                value={serviceAmounts.calendarSnapshotId}
                onChange={(event) => serviceAmounts.setCalendarSnapshotId(event.target.value)}
              >
                {serviceAmounts.academicYears.map((year) => (
                  <option key={year.calendarSnapshotId} value={year.calendarSnapshotId}>
                    {year.academicYear}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className="grid gap-2 sm:grid-cols-2">
            {serviceAmountFields.map(({ family, label }) => (
              <label
                key={family}
                className="flex flex-col gap-1.5 rounded-md border border-border bg-card p-3 text-[11.5px] font-bold"
              >
                <span className="flex items-center justify-between gap-2">
                  {t(label)}
                  {!serviceAmounts.enabled[family] && (
                    <small className="font-normal text-muted-foreground">
                      {t("sp.serviceNotEnabled")}
                    </small>
                  )}
                </span>
                <Input
                  className="chams-figure h-11"
                  type="number"
                  min="0"
                  step="1"
                  dir="ltr"
                  disabled={!serviceAmounts.available[family]}
                  value={serviceAmounts.values[family]}
                  onChange={(event) => serviceAmounts.setAmount(family, event.target.value)}
                />
              </label>
            ))}
          </div>
          <label className="flex flex-col gap-1.5 text-[11.5px] font-bold">
            {t("sp.reason")}
            <textarea
              className="min-h-20 rounded-md border border-input bg-card px-3 py-2.5 text-xs font-normal"
              required
              minLength={3}
              maxLength={500}
              value={serviceAmounts.reason}
              onChange={(event) => serviceAmounts.setReason(event.target.value)}
            />
          </label>
          <span className="text-[11px] text-muted-foreground">
            {t("sp.serviceAmountsPaidNote")}
          </span>
          {serviceAmounts.failed && (
            <span className="text-[11px] text-destructive">{operationsText.actionFailed}</span>
          )}
        </form>
      </Surface>

      {students.paymentActorId && (
        <DesktopPaymentFlow
          locale={locale}
          online={online}
          csrf={csrf ?? ""}
          student={student}
          payment={payment}
          synchronize={async () => {
            await workspace.sync();
          }}
          open={paymentOpen}
          onOpenChange={setPaymentOpen}
        />
      )}
      {students.paymentActorId && student.advanceYear && (
        <DesktopPaymentFlow
          locale={locale}
          online={online}
          csrf={csrf ?? ""}
          student={student}
          payment={advancePayment}
          yearContext={{ role: "next", academicYear: student.advanceYear.academicYear }}
          synchronize={async () => {
            await workspace.sync();
          }}
          open={advancePaymentOpen}
          onOpenChange={setAdvancePaymentOpen}
        />
      )}
      {students.paymentActorId && historicalYear && (
        <DesktopPaymentFlow
          locale={locale}
          online={online}
          csrf={csrf ?? ""}
          student={student}
          payment={historicalPayment}
          yearContext={{ role: "historical", academicYear: historicalYear.academicYear }}
          synchronize={async () => {
            await workspace.sync();
          }}
          open={historicalPaymentOpen}
          onOpenChange={setHistoricalPaymentOpen}
        />
      )}

      <Surface
        open={historyDocument !== null}
        onOpenChange={(open) => {
          if (!open) setHistoryDocument(null);
        }}
        title={historyDocument?.payment.reference ?? paymentText.history}
        closeLabel={t("common.close")}
        testId="payment-history-surface"
        wide
      >
        {historyDocument && (
          <div
            className="payment-history-document flex flex-col gap-3"
            data-document-preview-canvas
            dir="ltr"
          >
            <Badge dot variant="info" className="w-fit" dir={locale === "ar" ? "rtl" : "ltr"}>
              {paymentText[historyDocument.queueState]}
            </Badge>
            <A4DocumentPreview locale={locale} spatialDirection="ltr">
              <FinancialDocument locale={locale} snapshot={historyDocument.document} />
            </A4DocumentPreview>
          </div>
        )}
      </Surface>

      {online && csrf && (
        <OperationalPaymentSurface
          locale={locale}
          payment={operationalPayment}
          csrf={csrf}
          onOpenChange={(open) => {
            if (!open) setOperationalPayment(null);
          }}
          onChanged={() => void students.refreshSelected()}
        />
      )}

      {canAdminister && csrf && (
        <StudentFormSurface
          locale={locale}
          csrf={csrf}
          open={editOpen}
          student={student}
          classOptions={classOptionsOf(workspace)}
          onOpenChange={setEditOpen}
          onSaved={students.studentSaved}
          onOpenStudent={(id) => void students.openStudent(id)}
        />
      )}
    </section>
  );
}
